import { IconButton, InfoIcon, Typography, type TypographyProps } from "@povio/ui";
import { cx } from "class-variance-authority";
import clsx from "clsx";

import { HollowPencilIcon } from "@/assets/icons/ui/HollowPencil";
import ProfileNoSectionData from "@/components/home/sections/components/ProfileNoSectionData";
import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import { useInViewAnimation } from "@/hooks/useInViewAnimation";

export enum EditPageSection {
  Bio = "bio",
  Skills = "skills",
  WorkExperience = "work-experience",
  Education = "education",
  Certifications = "certifications",
  Volunteer = "volunteer",
  Recognition = "recognition",
}

interface IProps {
  title: string;
  titleClassName?: string;
  titleSize?: TypographyProps["size"];
  titleSizeMobile?: TypographyProps["sizeMobile"];
  children: React.ReactNode;
  isEmpty?: boolean;
  onEditSectionClicked?: () => void;
  className?: string;
  isPublic?: boolean;
  containerClassName?: string;
  cardClassName?: string;
  backgroundImageSrc?: string;
  backgroundClassName?: string;
  onSectionClicked?: () => void;
  enableInViewAnimation?: boolean;
  tooltip?: string;
  actionButton?: React.ReactNode;
  titleId?: string;
  titleActions?: React.ReactNode;
}

const ProfileSectionCard = ({
  title,
  children,
  titleClassName,
  titleActions,
  isEmpty = false,
  className,
  onEditSectionClicked,
  isPublic = false,
  containerClassName,
  cardClassName,
  backgroundImageSrc,
  backgroundClassName,
  titleSizeMobile = "title-4",
  onSectionClicked,
  enableInViewAnimation,
  tooltip,
  actionButton,
  titleSize = "title-4",
  titleId,
}: IProps) => {
  const { ref, animationClassName, visibilityClassName } = useInViewAnimation(enableInViewAnimation);
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={clsx(
        "print-avoid-break-inside relative flex w-full flex-col overflow-hidden",
        !isPublic && "rounded-xl bg-elevation-fill-default-2 shadow-5",
        cardClassName,
        onSectionClicked && "cursor-pointer",
        animationClassName,
        visibilityClassName,
      )}
      data-testid={`${title}-section`}
      onClick={onSectionClicked}
      ref={ref}
    >
      <div className={clsx("relative z-10 h-full p-4", containerClassName)}>
        {backgroundImageSrc && (
          <div
            className={clsx("absolute h-full w-full bg-cover bg-no-repeat brightness-90", backgroundClassName)}
            data-testid={`${title}-section-background`}
            style={{ backgroundImage: `url(${backgroundImageSrc})` }}
          />
        )}
        <div className="flex items-start justify-between">
          <Typography
            size={titleSize}
            sizeMobile={titleSizeMobile}
            variant="prominent-1"
            className={cx(titleClassName, "font-secondary", "z-10")}
            data-testid={`${title}-section-title`}
            id={titleId}
          >
            {title}
          </Typography>
          {tooltip && (
            <IconButton
              label={tooltip}
              icon={InfoIcon}
              size="none"
              variant="ghost"
              color="secondary"
              className="no-print"
            />
          )}
          {onEditSectionClicked && (
            <DemoClickWrapper>
              <IconButton
                variant="ghost"
                color="secondary"
                size="xs"
                icon={HollowPencilIcon}
                onPress={onEditSectionClicked}
                data-testid={`${title}-section-edit-button`}
                label="Edit"
                className="p-0!"
                disableTooltip
              />
            </DemoClickWrapper>
          )}
          {titleActions}
        </div>

        <div className={clsx("relative z-10 flex flex-col gap-6", className)}>
          {!isEmpty && children}
          {isEmpty && (
            <ProfileNoSectionData
              section={title}
              actionButton={actionButton}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSectionCard;
